import Big from 'big.js';
import * as smgCarts from 'smg/customer-api/v2/carts';
import { GetLineItems, LineItemMap, TypeMap, SizeMap } from 'smg/customer-api/lineitems';
import * as html from 'smg/html';
import Modal from 'smg/modal';
import * as models from 'smg/customer-api/models';
import { GetProofURLs } from 'smg/proofs';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import QueryString from 'smg/querystring';
import * as carts from '../carts';
import { Relref } from '../hugo';
import * as lineitems from '../lineitems';
import * as toast from 'smg/toast';

declare interface myWindow extends Window {
    readonly cloudFront: string;
}
declare const window: myWindow;

void main();

async function main() {
    const $spinner = document.getElementById('cart-spinner') as HTMLElement;
    const $content = document.getElementById('cart-content') as HTMLElement;
    const $cartItems = document.getElementById('cart-items') as HTMLElement;
    const $empty = document.getElementById('cart-empty') as HTMLElement;
    const $subtotal = document.getElementById('summary-subtotal') as HTMLElement;
    const $prepaymentRow = document.getElementById('cart-prepayment-row') as HTMLElement;
    const $prepayment = document.getElementById('summary-prepayment') as HTMLElement;
    const $availableDiscountRow = document.getElementById('cart-available-discount-row') as HTMLElement;
    const $availableDiscount = document.getElementById('summary-available-discount') as HTMLElement;
    const $total = document.getElementById('summary-total') as HTMLElement;

    // Pre-fetch line item maps
    void GetLineItems();

    // Handle query params: ?quote=, ?order=, ?cart= add to the existing cart
    const quoteID = QueryString().get('quote');
    if (quoteID) {
        await carts.AddCartItem({ quoteID }, {
            promptToCheckout: false,
            hideCartLink: true,
        });
    }
    const orderID = QueryString().get('order');
    if (orderID) {
        await carts.AddCartItem({ orderID }, {
            promptToCheckout: false,
            hideCartLink: true,
        });
    }
    const cartID = QueryString().get('cart');
    if (cartID) {
        await carts.AddCartItem({ cartID }, {
            promptToCheckout: false,
            hideCartLink: true,
        });
    }

    let currentCart: models.HyejooCart | undefined;

    void carts.GetCart('cartItems,totals').then(async cart => {
        $spinner.parentElement?.removeChild($spinner);

        const rawCartItems = cart?.cartItems;
        if (!cart || !rawCartItems?.length) {
            showEmpty();
            return;
        }

        currentCart = cart;
        updateSummary(cart);

        const cartItems = models.CartItem.fromArray(rawCartItems);

        const f = document.createDocumentFragment();
        for (const cartItem of cartItems) {
            f.appendChild(await renderCartItem(cartItem));
        }

        const lookupLink = document.createElement('p');
        lookupLink.className = 'text-center mt-2';
        lookupLink.innerHTML = `<a href="${Relref('/order')}" class="small font-weight-bold">&larr; Looking for another quote?</a>`;
        f.appendChild(lookupLink);

        $cartItems.appendChild(f);
        $content.classList.remove('d-none');
    }).catch(() => {
        $spinner.parentElement?.removeChild($spinner);
        showEmpty();
    });

    function showEmpty() {
        $empty.classList.remove('d-none');
    }

    function updateSummary(cart: models.HyejooCart) {
        currentCart = cart;
        $subtotal.textContent = DollarFormat(cart.totals.subtotal);
        // The server already nets any project deposit credit out of total, so
        // surface it as a negative line — otherwise total reads lower than
        // subtotal with nothing explaining the gap. Full deposit detail lives
        // on checkout; the cart just shows the applied amount.
        const availableDiscount = cart.totals.availableDiscount;
        if (!availableDiscount.eq(0)) {
            $availableDiscountRow.style.removeProperty('display');
            // The sign is the recorded line's, not the row's.
            if (availableDiscount.lt(0)) {
                $availableDiscount.textContent = `\u2212${DollarFormat(availableDiscount.abs())}`;
            } else {
                $availableDiscount.textContent = DollarFormat(availableDiscount);
            }
        } else {
            $availableDiscountRow.style.setProperty('display', 'none', 'important');
        }
        const prepayment = cart.totals.prepayment;
        if (prepayment.gt(0)) {
            $prepaymentRow.style.removeProperty('display');
            $prepayment.textContent = `\u2212${DollarFormat(prepayment)}`;
        } else {
            $prepaymentRow.style.setProperty('display', 'none', 'important');
        }
        $total.textContent = DollarFormat(cart.totals.total);
    }

    async function lineItemDescription(lineItem: models.QuoteLineItem | models.OrderLineItem): Promise<string> {
        const parts = [
            (await LineItemMap()).get(lineItem.lineItemID || '')?.networkNamePlural() || lineItem.lineItem || '',
            (await TypeMap()).get(lineItem.lineItemTypeID || '')?.networkName() || lineItem.type || '',
            (await SizeMap()).get(lineItem.lineItemTypeSizeID || '')?.networkName() || lineItem.size || '',
        ].filter(v => v.length > 0);

        return parts.join(' \u2013 ');
    }

    async function getProductDescription(items: (models.QuoteLineItem | models.OrderLineItem)[]): Promise<string> {
        const firstLI = items[0];
        if (!firstLI) {
            return '';
        }

        return lineItemDescription(firstLI);
    }

    async function renderCartItem(cartItem: models.CartItem): Promise<HTMLElement> {
        const el = template.Clone('#cart-item-template').firstElementChild as HTMLElement;
        const quote = cartItem.quote;
        const order = cartItem.order;
        const isOrder = Boolean(order) && !quote?.serial;

        // Serial/identifier + date
        const $serial = el.querySelector('.cart-item-serial') as HTMLElement;
        if (isOrder) {
            $serial.textContent = `Order #${order?.invoiceNumber ?? ''}`;
        } else {
            $serial.textContent = `#${quote?.serial ?? ''}`;
            if (order) {
                const $reorderOf = document.createElement('span');
                $reorderOf.className = 'small text-muted ml-2';
                $reorderOf.textContent = `Reorder of ${order.invoiceNumber}`;
                $serial.after($reorderOf);
            }
        }

        const $date = el.querySelector('.cart-item-date') as HTMLElement;
        if (cartItem.created?.isValid) {
            $date.textContent = cartItem.created.toLocaleString({ month: 'short', day: 'numeric', year: 'numeric' });
        }

        // Products + quantities — one row per top-level line item, matching the
        // proof & price quote breakdown. Multi-product quotes (e.g. Challenge
        // Coins + Lapel Pins) each get their own name and quantity.
        const $products = el.querySelector('.cart-item-products') as HTMLElement;
        let entityLineItems: (models.QuoteLineItem | models.OrderLineItem)[];
        if (isOrder) {
            entityLineItems = order?.orderLineItems ?? [];
        } else {
            entityLineItems = quote?.quoteLineItems ?? [];
        }

        if (entityLineItems.length) {
            for (const lineItem of entityLineItems) {
                const row = template.Clone('#cart-product-template').firstElementChild as HTMLElement;
                const $product = row.querySelector('.cart-item-product') as HTMLElement;
                const $qty = row.querySelector('.cart-item-qty') as HTMLElement;

                const desc = await lineItemDescription(lineItem);
                if (desc) {
                    $product.textContent = desc;
                }

                const qty = lineItem.quantity || 0;
                const productName = (await LineItemMap()).get(lineItem.lineItemID || '')?.networkNamePlural() || lineItem.lineItem || 'pcs';
                $qty.textContent = `\u00b7 ${qty.toLocaleString()} ${productName}`;

                $products.appendChild(row);
            }
        } else {
            const row = template.Clone('#cart-product-template').firstElementChild as HTMLElement;
            const $qty = row.querySelector('.cart-item-qty') as HTMLElement;
            $qty.textContent = `\u00b7 ${(cartItem.quantity || 0).toLocaleString()} pcs`;
            $products.appendChild(row);
        }

        // Price (with strike-through if discounted)
        const $price = el.querySelector('.cart-item-price') as HTMLElement;
        const sub = cartItem.subtotal ?? new Big(0);
        const maxSub = cartItem.maxNetworkSubtotal ?? new Big(0);
        if (!maxSub.eq(0) && maxSub.gt(sub)) {
            $price.innerHTML = `<span class="text-muted small"><s>${DollarFormat(maxSub)}</s></span> ${DollarFormat(sub)}`;
        } else {
            $price.textContent = DollarFormat(sub);
        }

        // Proofs — works for both quote proofs and order proofs
        const $proofsList = el.querySelector('.cart-item-proofs-list') as HTMLElement;
        const $noProofs = el.querySelector('.cart-item-no-proofs') as HTMLElement;

        interface proofLike { proof?: models.Proof; name?: string; number?: number | string }
        let proofs: proofLike[];
        if (isOrder) {
            proofs = order?.orderProofs ?? [];
        } else {
            proofs = quote?.quoteProofs ?? [];
        }

        if (proofs.length) {
            $noProofs.remove();
            const fancyboxGroup = `cart-${cartItem.id ?? ''}`;

            for (const qp of proofs) {
                const p = qp.proof;
                if (!p) {
                    continue;
                }

                const urls = GetProofURLs(window.cloudFront, p.file || '');
                const proofEl = template.Clone('#cart-proof-template').firstElementChild as HTMLElement;

                const $link = proofEl.querySelector('a') as HTMLAnchorElement;
                $link.href = urls.main;
                $link.setAttribute('data-fancybox', fancyboxGroup);

                const $img = $link.querySelector('img') as HTMLImageElement;
                $img.src = urls.thumb || urls.main;
                $img.alt = qp.name || `Proof ${qp.number ?? ''}`;

                const $label = proofEl.querySelector('.cart-proof-label') as HTMLElement;
                $label.textContent = `Proof ${qp.number ?? ''}`;

                $proofsList.appendChild(proofEl);
            }
        }

        // Action: View price breakdown
        el.querySelector('.cart-action-breakdown')?.addEventListener('click', () => {
            void openBreakdown(cartItem);
        });

        // Action: Remove with undo toast
        setupRemoveAction(el, cartItem);

        return el;
    }

    function setupRemoveAction(el: HTMLElement, cartItem: models.CartItem) {
        const $removeBtn = el.querySelector('.cart-action-remove') as HTMLButtonElement;
        if (!$removeBtn) {
            return;
        }

        $removeBtn.addEventListener('click', () => {
            el.style.display = 'none';

            let label: string;
            if (Boolean(cartItem.order) && !cartItem.quote?.serial) {
                label = `Order #${cartItem.order?.invoiceNumber ?? ''}`;
            } else {
                label = `#${cartItem.quote?.serial ?? ''}`;
            }
            const cartItemID = cartItem.id || '';

            // Delete immediately, restore on undo
            void smgCarts.DeleteCartItem(cartItemID).then(removed => {
                carts.SetCartCount(removed.cartItemsCount);
                toast.ShowCustom({
                    type: 'info',
                    message: `${label} removed from cart`,
                    actions: [{
                        label: 'Undo',
                        onClick: () => smgCarts.RestoreCartItem(cartItemID).then(restored => {
                            el.style.display = '';
                            $empty.classList.add('d-none');
                            $content.classList.remove('d-none');
                            carts.SetCartCount(restored.cartItemsCount);
                            void carts.GetCart('cartItems,totals').then(updatedCart => {
                                if (updatedCart) {
                                    updateSummary(updatedCart);
                                }
                            });
                        }).catch(() => {
                            toast.Error('Couldn\'t undo. Please re-add the item.');
                        }),
                    }],
                });

                void carts.GetCart('cartItems,totals').then(updatedCart => {
                    if (!updatedCart?.cartItems.length) {
                        $content.classList.add('d-none');
                        showEmpty();
                    } else {
                        updateSummary(updatedCart);
                    }
                });
            }).catch(() => {
                el.style.display = '';
                if (currentCart) {
                    updateSummary(currentCart);
                }
                toast.Error('Failed to remove item');
            });
        });
    }

    async function openBreakdown(cartItem: models.CartItem) {
        const quote = cartItem.quote;
        const order = cartItem.order;
        const isOrder = Boolean(order) && !quote?.serial;

        let entityLineItems: (models.QuoteLineItem | models.OrderLineItem)[];
        if (isOrder) {
            entityLineItems = order?.orderLineItems ?? [];
        } else {
            entityLineItems = quote?.quoteLineItems ?? [];
        }

        const desc = await getProductDescription(entityLineItems);
        const table = await lineitems.Table(entityLineItems);

        let breakdownLabel: string;
        if (isOrder) {
            breakdownLabel = `Order #${order?.invoiceNumber ?? ''}`;
        } else {
            breakdownLabel = `#${quote?.serial ?? ''}`;
        }

        const body = html.FromString(`
            <p class="mb-3">
                <code class="font-weight-bold">${html.Escape(breakdownLabel)}</code>
                <span class="text-muted small ml-1">${html.Escape(desc)}</span>
            </p>
            <div id="breakdown-table-container"></div>
            <p class="font-italic text-muted mt-3 mb-0" style="font-size:11px">*Sales tax and shipping calculated at checkout</p>
        `);

        const container = body.getElementById('breakdown-table-container') as HTMLElement;
        container.appendChild(table);

        new Modal({
            title: 'Price breakdown',
            body,
            size: 'modal-lg',
            cancelButton: false,
            close: 'Close',
        });
    }
}
